/* PDP quantity breaker + stepper + the buy-button price. Added by Oleg @ IAKOE.
   <ts-qty-breaker>: a card click sets the quantity in every product form on
   the page (main + sticky — kit forms carry a hidden input[name="quantity"]).
   <ts-qty-stepper>: fallback for products without tiers — own minus/input/plus
   markup (the kit ia-product-qty double-binds when moved in the DOM), relocates
   itself into the buy-button row and syncs the quantity.

   THE PRICE IN EVERY BUY BUTTON IS RENDERED HERE, in one place, from one set of
   rules, because four things move it: the qty cards, the stepper, Subscribe &
   Save (cadence / plan change) and the kit re-rendering the buy block. The kit
   writes its own single-unit price on those events first; we subscribe to the
   same events and paint the final state after it (a timeout, so we always run
   last whatever the module order is). The rules:
     total   = plan price x qty when a subscription is selected, else the active
               tier card's discounted total, else unit price x qty
     compare = the regular price (compare-at when the variant has one) x qty,
               struck only when it is actually higher than the total
   That covers "3 bottles saves $3" on a one-time purchase and "$99.45 instead of
   $117.00" on a subscription, and it never leaves a stale strike behind (the bug
   where 6 bottles -> 1 bottle painted "$39.00 $39.00"). */

import { EVENTS, subscribe } from 'ia-util-pubsub';

/* Only the page product's forms: the main buy block (<ia-main-product>) and the
   sticky bar (<ts-sticky-product>, which moves itself to <body>). The quick-add
   forms of recommendation cards are .product-form too, and a document-wide
   selector once wrote the Livener total and quantity into every "You may also
   like" button. */
const ownForms = (selector) =>
  document.querySelectorAll('ia-main-product .product-form ' + selector + ', ts-sticky-product .product-form ' + selector);

const setFormsQty = (qty) => {
  ownForms('input[name="quantity"]').forEach((input) => {
    input.value = qty;
  });
};

// Selected subscription plan price in cents when Subscribe & Save is active —
// qty projections use it instead of the one-time card totals (the qty-break
// discount is a one-time-purchase mechanic)
const activePlanCents = () => {
  const widget = document.querySelector('ia-product-subscription');
  if (!widget || widget.getAttribute('cadence') !== 'subscription') return null;
  const option = widget.querySelector('[data-selling-plan-select]')?.selectedOptions?.[0];
  const cents = parseInt(option?.dataset.planPriceCents, 10);
  return isNaN(cents) ? null : cents;
};

// Money formatter (reused by stepper + servings; currency from a controller el)
const moneyFmt = (currency) =>
  new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: currency || 'GBP',
  });

/* Dynamic servings: count = servings_per_bottle × qty, price-each = total / count.
   Updates every [data-ts-servings] (the title badge + the pill). totalCents is
   the selected (possibly discounted) total; falls back to unit × qty. */
const updateServings = (qty, totalCents, currency) => {
  const fmt = moneyFmt(currency);
  document.querySelectorAll('[data-ts-servings]').forEach((el) => {
    const spb = parseInt(el.dataset.spb, 10) || 0;
    if (!spb) return;
    const count = spb * qty;
    const unit = parseInt(el.dataset.unit, 10) || 0;
    const total = totalCents != null ? totalCents : unit * qty;
    const countEl = el.querySelector('[data-ts-servings-count]');
    const eachEl = el.querySelector('[data-ts-servings-each]');
    if (countEl) countEl.textContent = count + ' servings';
    if (eachEl) {
      const each = fmt.format(total / count / 100);
      // pill wraps it in parens, badge doesn't — keep the surrounding text style
      eachEl.textContent = eachEl.textContent.trim().startsWith('(') ? '(' + each + ' ea)' : each + ' ea';
    }
  });
};

/* The qty UI (breaker or stepper) is also where the unit prices live, so it is
   the controller for the whole price projection. */
const controller = () => document.querySelector('ts-qty-breaker, ts-qty-stepper');

const currentQty = (el) => {
  const card = el.querySelector?.('.ts-qty__card.is-active');
  if (card) return parseInt(card.dataset.qty, 10) || 1;
  const input = el.querySelector?.('.ts-qty-stepper__input');
  return parseInt(input?.value, 10) || 1;
};

const paint = (saleText, compareText) => {
  ownForms('.ia-price').forEach((box) => {
    const saleEl = box.querySelector('[data-sale-price]');
    if (saleEl) saleEl.textContent = saleText;
    let strike = box.querySelector('[data-sale-price-compare]');
    if (!compareText) {
      if (strike) strike.style.display = 'none';
      return;
    }
    if (!strike && saleEl) {
      strike = document.createElement('strike');
      strike.setAttribute('data-sale-price-compare', '');
      saleEl.parentNode.insertBefore(strike, saleEl);
    }
    if (strike) {
      strike.textContent = compareText;
      strike.style.display = '';
    }
  });
};

const render = () => {
  const el = controller();
  if (!el) return;
  const qty = currentQty(el);
  const unit = parseInt(el.dataset.unitPrice, 10) || 0;
  const regular = parseInt(el.dataset.unitCompare, 10) || unit;
  const fmt = moneyFmt(el.dataset.currency);
  const plan = activePlanCents();
  const card = el.querySelector?.('.ts-qty__card.is-active');

  let total;
  if (plan != null) {
    total = plan * qty;
  } else if (card) {
    total = parseInt(card.dataset.total, 10) || unit * qty;
  } else {
    total = unit * qty;
  }
  const compare = regular * qty;
  paint(fmt.format(total / 100), compare > total ? fmt.format(compare / 100) : null);
  updateServings(qty, total, el.dataset.currency);
};

// The kit paints its own single-unit price on these events; run after it, and
// after its [data-re-render] swap of the buy block and the sticky bar.
const renderLater = () => setTimeout(render, 0);
subscribe(EVENTS.cadenceChange, renderLater);
subscribe(EVENTS.planChange, renderLater);
subscribe(EVENTS.variantChange, renderLater);

if (!customElements.get('ts-qty-breaker')) {
  customElements.define(
    'ts-qty-breaker',
    class TsQtyBreaker extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        this.addEventListener('click', (event) => {
          const card = event.target.closest('.ts-qty__card');
          if (!card || !this.contains(card)) return;

          this.querySelectorAll('.ts-qty__card').forEach((c) => {
            const active = c === card;
            c.classList.toggle('is-active', active);
            c.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          setFormsQty(parseInt(card.dataset.qty, 10) || 1);
          render();
        });
        renderLater();
      }
    }
  );
}

if (!customElements.get('ts-qty-stepper')) {
  customElements.define(
    'ts-qty-stepper',
    class TsQtyStepper extends HTMLElement {
      connectedCallback() {
        if (!this._init) {
          this._init = true;
          this._input = this.querySelector('.ts-qty-stepper__input');

          this.addEventListener('click', (event) => {
            const btn = event.target.closest('[data-step]');
            if (!btn || !this.contains(btn)) return;
            this.setQty(parseInt(this._input.value, 10) + parseInt(btn.dataset.step, 10));
          });
          this._input.addEventListener('change', () => {
            this.setQty(parseInt(this._input.value, 10));
          });
        }
        this.moveIntoForm();
      }

      setQty(qty) {
        if (isNaN(qty) || qty < 1) qty = 1;
        this._input.value = qty;
        setFormsQty(qty);
        render();
      }

      moveIntoForm() {
        const form = document.querySelector('.ia-main-product__info .product-form');
        if (!form || form.contains(this)) return;
        const addButton = form.querySelector('button[name="add"]');
        if (!addButton) return;
        form.classList.add('has-ts-stepper');
        form.insertBefore(this, addButton);

        // The kit re-renders the buy block on variant change ([data-re-render]
        // innerHTML swap) which would drop us — watch and re-insert.
        const host = form.closest('[data-re-render]');
        if (host && !this._observer) {
          this._observer = new MutationObserver(() => {
            if (!host.contains(this)) this.moveIntoForm();
          });
          this._observer.observe(host, { childList: true, subtree: true });
        }
      }
    }
  );
}
